import React, { useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { FaExternalLinkAlt, FaUndo } from "react-icons/fa";
import "../product/AdminProducts.css";
import { POLICIES, policyTemplate } from "../../../util/policyDefaults";

/**
 * Settings → Policies: the store's privacy, refund, shipping and terms
 * pages. Each starts from a template; {store}, {email}, {phone} and
 * {address} are filled in from Store settings when shown.
 */
export default function PolicySettings() {
  const [texts, setTexts] = useState(null);
  const [active, setActive] = useState(POLICIES[0].type);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Policies — Admin";
    axios.get("/api/admin/settings").then((res) => {
      const s = res.data.settings || {};
      const next = {};
      POLICIES.forEach((p) => {
        next[p.key] = String(s[p.key] || "").trim() || policyTemplate(p.type);
      });
      setTexts(next);
    });
  }, []);

  const save = () => {
    setSaving(true);
    const body = {};
    POLICIES.forEach((p) => {
      // Saving the untouched template keeps the page following future template updates.
      body[p.key] = texts[p.key].trim() === policyTemplate(p.type).trim() ? "" : texts[p.key];
    });
    axios.post("/api/admin/settings", body)
      .then((res) => swal(res.data.status === 200 ? "Saved" : "Not saved", res.data.status === 200 ? "Policies saved." : res.data.message, res.data.status === 200 ? "success" : "error"))
      .catch(() => swal("Not saved", "Please try again.", "error"))
      .finally(() => setSaving(false));
  };

  if (!texts) {
    return <div className="ap"><div className="p-5 text-center"><div className="spinner-border text-primary" role="status" /></div></div>;
  }
  const policy = POLICIES.find((p) => p.type === active);

  return (
    <div className="ap">
      <div className="ap-head">
        <div>
          <h4>Policies</h4>
          <p>Your store's privacy, refund, shipping and terms pages. JazzCash and Easypaisa check for these before approving a store.</p>
        </div>
        <div className="ap-head-actions">
          <button type="button" className="ap-btn ap-btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save policies"}</button>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {POLICIES.map((p) => (
          <button type="button" key={p.type} className={`ap-btn ${active === p.type ? "ap-btn-dark" : ""}`} onClick={() => setActive(p.type)}>{p.title}</button>
        ))}
      </div>

      <section className="ap-card p-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div className="small text-muted">
            Shown at <code>/policies/{policy.type}</code>. Use <code># </code> for a heading, a blank line between paragraphs, and <code>{"{store}"}</code>, <code>{"{email}"}</code>, <code>{"{phone}"}</code>, <code>{"{address}"}</code> for your details.
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="ap-btn" onClick={() => setTexts((t) => ({ ...t, [policy.key]: policyTemplate(policy.type) }))}>
              <FaUndo size={10} /> Use template
            </button>
            <a className="ap-btn" href={`/policies/${policy.type}`} target="_blank" rel="noreferrer"><FaExternalLinkAlt size={10} /> View page</a>
          </div>
        </div>
        <textarea
          className="form-control"
          style={{ minHeight: 420, fontSize: 13.5, lineHeight: 1.6, fontFamily: "inherit" }}
          value={texts[policy.key]}
          onChange={(e) => setTexts((t) => ({ ...t, [policy.key]: e.target.value }))}
          aria-label={policy.title}
        />
        <p className="small text-muted mt-2 mb-0">These templates are a starting point, not legal advice. Change the return days and delivery times to match how your store works.</p>
      </section>
    </div>
  );
}
